<script setup>
// 投票挺步道：對應設計稿「投票挺步道」。
// 上半是活動橫幅（票選時間、票數規則與四區今日投票狀態），下半是分區頁籤 + 搜尋列 + 步道卡片。
//
// 資料取自 /front/trail（公開介面，SSR 期間就取回）：分區頁籤、縣市下拉、該區步道與票數
// 一次帶回；登入時另外帶回自己今天的四區配額，橫幅的狀態燈與卡片的「今日已投」都看它。
// 投票走 /front/vote，成功後直接拿回傳的最新配額與票數更新畫面，不重打列表。
// 分享加碼（/front/vote/share）目前只有後端，這一頁沒有分享入口。
// 同一條步道可以重複投，投票鈕只看該分區今天還有沒有票。
// 介面掛掉或系統還沒有活動時，橫幅退回設計稿的假文案、列表留白，版面不會開天窗。
import bannerBg from '~/assets/img/vote-banner-bg.png'
import bannerHeart from '~/assets/img/vote-banner-heart.png'
import bannerEyes from '~/assets/img/vote-banner-eyes.png'
import hikerLeft from '~/assets/img/vote-hiker-left.png'
import hikerRight from '~/assets/img/vote-hiker-right.png'
import zonePillBg from '~/assets/img/vote-zone-pill.png'
import zoneCheckIcon from '~/assets/img/vote-zone-check.png'
import searchIcon from '~/assets/img/icon-search.png'
import pinIcon from '~/assets/img/icon-pin.png'
import starIcon from '~/assets/img/icon-star.png'
import pinTopIcon from '~/assets/img/icon-pin-top.png'
import pinTopOffIcon from '~/assets/img/icon-pin-top-off.png'
import voteIcon from '~/assets/img/icon-vote.png'
import { VOTE_BUTTON_ENABLED } from '~/utils/voteSwitch'
import voteDoneArt from '~/assets/img/trail-vote-done.png'
import banner from '~/assets/img/banner/banner2.png'

definePageMeta({ layout: false })

const config = useRuntimeConfig()
useHead({ title: config.public.siteName })

const api = useApi()
const route = useRoute()
const router = useRouter()
const { resolveFileUrl } = useFileUrl()
const { isLogged } = useAuth()
const { openLogin } = useLoginDialog()
const { ensureProfileComplete, goCompleteProfile, isProfileIncomplete } = useProfileGate()
const { lockScroll } = useScrollLock()

/* ---------------- 篩選條件 ---------------- */

const SORTS = [
  // 預設隨機：後端每次請求都重洗，每條步道都有機會排在最前面
  { value: 'random', label: '隨機排序' },
  { value: 'votes', label: '依票數排序' },
  // { value: 'rating', label: '依評分排序' },
  { value: 'name', label: '依名稱排序' }
]

// 分區同步到網址（?zone=north），分享出去與重整都還停在同一區
const zone = ref(typeof route.query.zone === 'string' ? route.query.zone : '')
const sort = ref('random')
// 關鍵字按下「搜尋」才套用：邊打邊查會讓每個字都打一次後端
const keyword = ref('')
const search = ref('')

const { data, pending } = await useAsyncData(
  'vote-trails',
  () =>
    api
      .get('/front/trail', {
        zone: zone.value || undefined,
        keyword: search.value || undefined,
        sort: sort.value
      })
      .catch(() => null),
  { watch: [zone, sort, search] }
)

function switchZone(key) {
  if (zone.value === key) return
  zone.value = key
  router.replace({ query: key ? { zone: key } : {} })
}

/* 排序下拉：原生 <select> 展開後的選單由作業系統畫，選中樣式與圓角都改不動，
   因此換成自製 listbox，收合與展開的樣式才能跟設計稿的膠囊一致 */
const sortOpen = ref(false)
// 鍵盤操作的游標，與滑鼠 hover 分開記，用上下鍵時不會被游標位置搶走
const sortCursor = ref(0)
const sortBox = ref(null)
const sortLabel = computed(() => SORTS.find((s) => s.value === sort.value)?.label || '')

function openSort() {
  sortCursor.value = Math.max(
    0,
    SORTS.findIndex((s) => s.value === sort.value)
  )
  sortOpen.value = true
}

function pickSort(value) {
  sort.value = value
  sortOpen.value = false
}

function onSortKeydown(e) {
  if (e.key === 'Escape') return (sortOpen.value = false)
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    return sortOpen.value ? pickSort(SORTS[sortCursor.value].value) : openSort()
  }
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
  e.preventDefault()
  if (!sortOpen.value) return openSort()
  const step = e.key === 'ArrowDown' ? 1 : -1
  sortCursor.value = (sortCursor.value + step + SORTS.length) % SORTS.length
}

// 點到別處就收起來；只在展開時才掛監聽，免得整頁都在跑這個 handler
function onDocPointer(e) {
  if (!sortBox.value?.contains(e.target)) sortOpen.value = false
}

watch(sortOpen, (open) => {
  if (open) return document.addEventListener('pointerdown', onDocPointer)
  document.removeEventListener('pointerdown', onDocPointer)
})

function onSearch() {
  search.value = keyword.value.trim()
}

/* ---------------- 橫幅 ---------------- */

const activity = computed(() => data.value?.activity || null)
const votable = computed(() => Boolean(data.value?.votable))

// 設計稿的日期是民國年（115年09月20日）；活動只存日期，時分不入版
function rocDate(date, withYear = true) {
  if (!date) return ''
  const [y, m, d] = String(date).slice(0, 10).split('-')
  if (!y || !m || !d) return ''
  return withYear ? `${Number(y) - 1911}年${m}月${d}日` : `${m}月${d}日`
}

// 起訖各自成段：手機上一行放不下時斷在「至」的兩側，而不是把日期從中間拆開
const period = computed(() => {
  const a = activity.value
  if (!a?.start_date || !a?.end_date) return { from: '115年09月20日', to: '10月16日' }
  const sameYear = String(a.start_date).slice(0, 4) === String(a.end_date).slice(0, 4)
  return { from: rocDate(a.start_date), to: rocDate(a.end_date, !sameYear) }
})

// 沒有進行中的活動、拿到的是最近一檔已結束的那一檔：橫幅整塊換成截止公告
const ended = computed(
  () => !!activity.value && !activity.value.ongoing && !activity.value.upcoming
)

// 活動只存日期、不存時分，所以截止時刻固定寫成結束日當天的 23:59（＝那一天的最後一分鐘）
const endedPeriodText = computed(
  () => `網路票選期間已於 ${rocDate(activity.value?.end_date) || '活動結束日'} 23:59 截止`
)

// 票選結束後的說明：票還是照投（排行榜持續累計），只是不再發加碼票與抽獎機會
const ENDED_NOTE =
  '感謝山友的熱情參與！您仍可持續投票支持喜愛的步道，唯即日起不再提供加碼票數與抽獎機會。'

// 橫幅的兩行說明是版面文案（活動辦法頁才是後台維護的完整規則），與設計稿一字不差
const NOTES = [
  {
    label: '基本票數',
    text: '每人每天可於 4 個區域(北、中、南、東)各投 1 票(共 4 票,不可重複投給同一條步道)。'
  },
  {
    label: '注意事項',
    text: '當日票數須於當日使用完畢,無法累積至隔日;投票前請先確認,投票後無法修改。'
  }
]

// 系統還沒有分區資料時照設計稿的四區渲染，頁籤與狀態燈才不會整排消失
const FALLBACK_ZONES = [
  { id: -1, zone_key: 'north', zone_name: '北區' },
  { id: -2, zone_key: 'central', zone_name: '中區' },
  { id: -3, zone_key: 'south', zone_name: '南區' },
  { id: -4, zone_key: 'east', zone_name: '東區' }
]

const zones = computed(() => (data.value?.zones?.length ? data.value.zones : FALLBACK_ZONES))
const currentZone = computed(() => data.value?.zone?.zone_key || zones.value[0]?.zone_key || '')

// 今日配額：登入才有，投完票後由投票介面回傳的最新配額覆蓋，不必重打列表
const quota = ref(null)
watch(data, (d) => (quota.value = d?.quota || null), { immediate: true })

const quotaByZone = computed(() => new Map((quota.value?.zones || []).map((z) => [z.zone_id, z])))

// 四區狀態燈：沒登入一律顯示「尚可投票」，登入後才依自己的配額標記。
// 分享加碼後一區有兩票，所以「投過了」與「投完了」是兩件事，燈號看的是還有沒有票
const zoneStatus = computed(() =>
  zones.value.map((z) => {
    const zq = quotaByZone.value.get(z.id)
    const used = zq?.used || 0
    const remaining = zq ? zq.remaining : 1
    // 整句在這裡串好：擺進樣板會被格式化拆行，中間點兩側就多出空白
    let text = '尚可投票'
    if (used > 0) text = remaining > 0 ? `還可投${remaining}票` : `已投${used}票`
    return {
      key: z.zone_key,
      label: `${z.zone_name}·${text}`,
      voted: used > 0 && remaining === 0
    }
  })
)

/* ---------------- 步道卡片 ---------------- */

// 投票後只有那一張卡的票數會變，整份列表不必重打
const voteBumps = ref({})
watch(data, () => (voteBumps.value = {}))

const trails = computed(() => {
  const list = (data.value?.trails || []).map((t) => ({
    id: t.id,
    name: t.trail_name,
    area: [t.city, t.district].filter(Boolean).join(''),
    zoneId: t.zone_id,
    zoneName: t.zone_name,
    level: t.difficulty || '—',
    length: t.distance === null || t.distance === undefined ? '—' : `${Number(t.distance)}km`,
    duration: t.duration || '—',
    // 未評分（0 或空）就不印星星，卡片上不會出現一顆「0.0」
    rating: Number(t.rating) > 0 ? Number(t.rating).toFixed(1) : '',
    tags: t.tags || [],
    votes: Number(voteBumps.value[t.id] ?? t.votes ?? 0),
    // 後端的 cover 就是圖片陣列的第一張，卡片只印它，整組圖要進詳情頁才看得到
    cover: resolveFileUrl(t.cover)
  }))
  // 置頂的浮到最前面；sort 是穩定排序，其餘步道維持後端給的順序
  return list.sort(
    (a, b) => Number(pinned.value.includes(b.id)) - Number(pinned.value.includes(a.id))
  )
})

// **同一條步道可以重複投**：按鈕只看這一區今天還有沒有票，投過誰不影響
// （未登入拿不到配額，按鈕照樣亮著，按下去先帶出登入彈窗）
function voteState(trail) {
  if (!votable.value) {
    return { label: activity.value?.upcoming ? '投票尚未開始' : '投票已結束', disabled: true }
  }
  const zq = quotaByZone.value.get(trail.zoneId)
  if (zq && zq.remaining <= 0) return { label: '今日票數已用完', disabled: true }
  return { label: '我要投票', disabled: false }
}

/* ---------------- 置頂 ---------------- */

// 置頂只存在這台瀏覽器：後端沒有這張表，它是「把我在意的步道釘在列表最前面」的
// 個人便利功能，不影響任何人看到的排序，換裝置或清快取就會不見
const PIN_KEY = 'trail_pins'
const pinned = ref([])

function readPinned() {
  try {
    const raw = JSON.parse(localStorage.getItem(PIN_KEY) || '[]')
    return Array.isArray(raw) ? raw.map(Number) : []
  } catch {
    return []
  }
}

// SSR 讀不到 localStorage，一律先以「未置頂」渲染，掛載後才補上真正的狀態，
// 這樣兩邊第一次渲染的結果一致，不會有 hydration 警告
onMounted(() => {
  pinned.value = readPinned()
})

function togglePin(id) {
  // 新釘的放最前面，連續釘幾條時順序才跟得上直覺
  const next = pinned.value.includes(id)
    ? pinned.value.filter((x) => x !== id)
    : [id, ...pinned.value]
  try {
    localStorage.setItem(PIN_KEY, JSON.stringify(next))
    pinned.value = next
  } catch {
    // 無痕模式等寫不進去的情況：不改狀態，免得畫面說置頂了其實沒存到
    dialog.value = { art: false, title: '', text: '這個瀏覽器不允許儲存置頂', trail: '' }
  }
}

/* ---------------- 投票 ---------------- */

const voting = ref(0)
// 投票成功（設計稿「投票成功」）與各種提示共用同一個彈窗，只差有沒有那張插圖
const dialog = ref(null)
// 投票前先問「投幾票、確定嗎」（設計稿「投票彈窗」），按下確認才真的送出
const voteConfirm = ref(null)

async function onVote(trail) {
  if (voting.value) return
  // 投票端點只認參與者 token，沒登入就先帶出登入彈窗
  if (!isLogged.value) return openLogin()
  // 個人資料沒填齊就先去會員中心補，補完會回到這一頁
  if (!(await ensureProfileComplete())) return

  const zoneQuota = quotaByZone.value.get(trail.zoneId)
  voteConfirm.value = {
    id: trail.id,
    name: trail.name,
    cover: trail.cover,
    zoneName: trail.zoneName || zoneQuota?.zone_name || '',
    // 未登入或拿不到配額時交給彈窗自己退回 1，這裡不硬湊一個數字
    remaining: zoneQuota?.remaining
  }
}

function closeConfirm() {
  if (voting.value) return
  voteConfirm.value = null
}

async function submitVote(votes = 1) {
  const target = voteConfirm.value
  if (!target || voting.value) return

  voting.value = target.id
  try {
    const res = await api.post('/front/vote', { trail_id: target.id, votes })
    quota.value = res
    voteBumps.value = { ...voteBumps.value, [target.id]: res.trail.votes }
    voteConfirm.value = null
    dialog.value = {
      art: true,
      title: '投票成功',
      text: `已將您在${res.trail.zone_name}的今日 ${votes} 票投給`,
      trail: res.trail.trail_name
    }
  } catch (err) {
    voteConfirm.value = null
    if (isProfileIncomplete(err)) return goCompleteProfile()
    dialog.value = { art: false, title: '', text: err.message, trail: '' }
  } finally {
    voting.value = 0
  }
}

function closeDialog() {
  dialog.value = null
}

function onKeydown(e) {
  if (e.key === 'Escape') closeDialog()
}

watch(dialog, (open) => {
  if (open) {
    window.addEventListener('keydown', onKeydown)
    lockScroll(true)
    return
  }
  window.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('pointerdown', onDocPointer)
})
</script>

<template>
  <div class="vote">
    <div class="head-fix">
      <SiteHeader active="vote" />
    </div>

    <!-- 橫幅：風景是整張底圖，兩位登山客是獨立素材，一律貼齊橫幅底緣 -->
    <!--
    <section class="banner" :style="{ backgroundImage: `url(${bannerBg})` }">
      <img class="hiker hiker-l" :src="hikerLeft" alt="" aria-hidden="true" />
      <img class="hiker hiker-r" :src="hikerRight" alt="" aria-hidden="true" />

      <div class="hero">
        <h1 class="title">
          <img class="title-heart" :src="bannerHeart" alt="" aria-hidden="true" />
          <span>投票挺步道</span>
          <img class="title-eyes" :src="bannerEyes" alt="" aria-hidden="true" />
        </h1>

        <p v-if="ended" class="period">{{ endedPeriodText }}</p>
        <p v-else class="period">
          網路票選時間:<span>{{ period.from }}</span> 至 <span>{{ period.to }}</span>
        </p>

        <div class="notes">
          <p v-if="ended">{{ ENDED_NOTE }}</p>
          <template v-else>
            <p v-for="n in NOTES" :key="n.label">
              <b>{{ n.label }}：</b>{{ n.text }}
            </p>
          </template>
        </div>

        <ul class="zstat" :style="{ '--zpill-bg': `url(${zonePillBg})` }">
          <li v-for="z in zoneStatus" :key="z.key" class="zpill" :class="{ 'is-done': z.voted }">
            <img v-if="z.voted" class="zcheck" :src="zoneCheckIcon" alt="" aria-hidden="true" />
            <span>{{ z.label }}</span>
          </li>
        </ul>
      </div>
    </section>
    -->
    <section>
      <img
        style="width: 100%; height: auto; display: block"
        :src="banner"
        alt=""
        aria-hidden="true"
      />
    </section>

    <main class="body">
      <div class="wrap">
        <!-- 分區頁籤 -->
        <div class="tabs" role="tablist" aria-label="步道分區">
          <button
            v-for="z in zones"
            :key="z.zone_key"
            class="tab"
            :class="{ 'is-on': currentZone === z.zone_key }"
            type="button"
            role="tab"
            :aria-selected="currentZone === z.zone_key"
            @click="switchZone(z.zone_key)"
          >
            {{ z.zone_name }}
          </button>
        </div>

        <!-- 搜尋與排序 -->
        <div class="tools">
          <form class="search" @submit.prevent="onSearch">
            <img class="search-ico" :src="searchIcon" alt="" aria-hidden="true" />
            <input
              v-model="keyword"
              class="search-input"
              type="search"
              placeholder="搜尋步道名稱關鍵字"
              aria-label="搜尋步道名稱關鍵字"
            />
            <button class="search-btn" type="submit">搜尋</button>
          </form>

          <div ref="sortBox" class="selects">
            <div class="select-box" :class="{ 'is-open': sortOpen }">
              <button
                class="select"
                type="button"
                aria-label="排序方式"
                aria-haspopup="listbox"
                :aria-expanded="sortOpen"
                @click="sortOpen ? (sortOpen = false) : openSort()"
                @keydown="onSortKeydown"
              >
                <span class="select-text">{{ sortLabel }}</span>
                <svg class="select-caret" viewBox="0 0 12 8" aria-hidden="true">
                  <path
                    d="M1 1.5 6 6.5 11 1.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              <Transition name="pop">
                <ul v-if="sortOpen" class="select-menu" role="listbox" aria-label="排序方式">
                  <li
                    v-for="(s, i) in SORTS"
                    :key="s.value"
                    class="select-opt"
                    :class="{ 'is-on': s.value === sort, 'is-cursor': i === sortCursor }"
                    role="option"
                    :aria-selected="s.value === sort"
                    @mouseenter="sortCursor = i"
                    @click="pickSort(s.value)"
                  >
                    <span>{{ s.label }}</span>
                    <svg class="select-tick" viewBox="0 0 14 14" aria-hidden="true">
                      <path
                        d="M2.5 7.5 5.5 10.5 11.5 3.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </li>
                </ul>
              </Transition>
            </div>
          </div>
        </div>

        <!-- 步道卡片 -->
        <ul v-if="trails.length" class="cards" :class="{ 'is-loading': pending }">
          <li v-for="t in trails" :key="t.id" class="card">
            <div class="cover">
              <!-- 置頂鈕與分區牌壓在封面上，按鈕不能包進連結裡，因此連結只蓋圖片本身 -->
              <NuxtLink class="cover-link" :to="`/trails/${t.id}`" :aria-label="t.name">
                <img v-if="t.cover" class="cover-img" :src="t.cover" :alt="t.name" loading="lazy" />
                <span v-else class="cover-img cover-empty" aria-hidden="true"></span>
              </NuxtLink>

              <!-- <button
                class="pin"
                :class="{ 'is-on': pinned.includes(t.id) }"
                type="button"
                :aria-pressed="pinned.includes(t.id)"
                :aria-label="pinned.includes(t.id) ? `取消置頂${t.name}` : `置頂${t.name}`"
                :title="pinned.includes(t.id) ? '取消置頂' : '置頂'"
                @click="togglePin(t.id)"
              >
                <img :src="pinned.includes(t.id) ? pinTopOffIcon : pinTopIcon" alt="" />
              </button> -->

              <span class="cover-zone">
                <img :src="pinIcon" width="24" height="24" alt="" aria-hidden="true" />
                {{ t.zoneName }}
              </span>
            </div>

            <div class="sub">
              <p class="area">{{ t.area }}</p>
              <!-- <p v-if="t.rating" class="rating">
                <img :src="starIcon" width="18" height="18" alt="" aria-hidden="true" />
                {{ t.rating }}
              </p> -->
            </div>
            <h2 class="name">
              <NuxtLink :to="`/trails/${t.id}`">{{ t.name }}</NuxtLink>
            </h2>

            <div class="meta">
              <!-- 一組欄位包成一個 span：卡片變窄時斷在欄位之間，
                   不會把「預計時長：」和它的值拆到兩行 -->
              <p class="specs">
                <span class="spec"
                  ><b>級數：</b><i>{{ t.level }}</i></span
                >
                <span class="spec"
                  ><b>單程：</b><i>{{ t.length }}</i></span
                >
                <span class="spec"
                  ><b>預計時長：</b><i>{{ t.duration }}</i></span
                >
              </p>
              <ul v-if="t.tags.length" class="tags">
                <li v-for="tag in t.tags" :key="tag">{{ tag }}</li>
              </ul>
            </div>

            <div class="foot">
              <p class="votes">
                <b><CountUp :value="t.votes" /></b>
                目前票數
              </p>
              <button
                v-if="VOTE_BUTTON_ENABLED"
                class="vote-btn"
                type="button"
                :disabled="voteState(t).disabled || voting === t.id"
                @click="onVote(t)"
              >
                <img :src="voteIcon" width="24" height="24" alt="" aria-hidden="true" />
                {{ voting === t.id ? '投票中…' : voteState(t).label }}
              </button>
            </div>
          </li>
        </ul>

        <p v-else class="empty">
          {{ pending ? '載入中…' : '這一區目前沒有符合條件的步道' }}
        </p>
      </div>
    </main>

    <SiteFooter />

    <!-- 每次投票前先確認張數（設計稿「投票彈窗」），按下確認才真的送出 -->
    <VoteConfirmDialog
      :open="!!voteConfirm"
      :trail-name="voteConfirm?.name || ''"
      :cover="voteConfirm?.cover || ''"
      :zone-name="voteConfirm?.zoneName || ''"
      :remaining="voteConfirm?.remaining"
      :submitting="!!voting"
      @close="closeConfirm()"
      @confirm="submitVote"
    />

    <!-- 投票成功與各種提示共用同一個彈窗（設計稿「投票成功」） -->
    <ClientOnly>
      <Teleport to="body">
        <Transition name="dlg">
          <div v-if="dialog" class="dlg-mask" @click.self="closeDialog()">
            <div class="dlg-panel" role="alertdialog" aria-modal="true" aria-labelledby="dlg-text">
              <span v-if="dialog.art" class="dlg-art">
                <img :src="voteDoneArt" alt="" aria-hidden="true" />
              </span>
              <h2 v-if="dialog.title" class="dlg-title">{{ dialog.title }}</h2>
              <p id="dlg-text" class="dlg-text">
                {{ dialog.text }}<b v-if="dialog.trail">{{ dialog.trail }}</b>
              </p>
              <button class="dlg-btn" type="button" @click="closeDialog()">確認</button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style scoped>
.vote {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 頁首釘在畫面最上方，內容捲動時不跟著走（與活動辦法各頁一致） */
.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

/* ---------------- 橫幅 ---------------- */

/* 底圖就是 1440 × 478：內容用一般流排版，字多了橫幅自己長高，
   窄螢幕不會把說明壓在人物身上 */
.banner {
  position: relative;
  min-height: 478px;
  padding: 95px 20px 60px;
  background-position: center bottom;
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
}

/* 兩位登山客貼齊橫幅底緣，寬度跟著版面縮放，與底圖同步 */
.hiker {
  position: absolute;
  bottom: 0;
  display: block;
  width: 16.6%;
  max-width: 240px;
  pointer-events: none;
  user-select: none;
}

.hiker-l {
  left: 12.5%;
  width: 15.6%;
}

.hiker-r {
  right: 10.6%;
}

.hero {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
}

/* 標題兩側的紅心與眼睛是切邊裝飾，跟著標題一起縮 */
.title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0;
  color: #f5ad66;
  font-size: clamp(38px, 5vw, 72px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 2px;
  /* 設計稿是咖啡色描邊；-webkit-text-stroke 會往字身內縮，中文筆畫會糊成一團，
     所以改用八向陰影模擬（與活動辦法橫幅同一手法） */
  text-shadow:
    -2px 0 #6b4a2b,
    2px 0 #6b4a2b,
    0 -2px #6b4a2b,
    0 2px #6b4a2b,
    -2px -2px #6b4a2b,
    2px -2px #6b4a2b,
    -2px 2px #6b4a2b,
    2px 2px #6b4a2b,
    4px 6px 0 rgba(107, 74, 43, 0.18);
}

.title-heart {
  align-self: flex-start;
  width: 28px;
  margin-top: 6px;
}

.title-eyes {
  align-self: flex-end;
  width: 49px;
  margin-bottom: 2px;
}

.period {
  margin: 12px 0 0;
  color: #000000;
  font-size: clamp(17px, 1.8vw, 26px);
  font-weight: 700;
  line-height: 1.4;
}

/* 日期本身不斷行，要斷就斷在「至」的兩側 */
.period span {
  white-space: nowrap;
}

/* 說明條：設計稿是淡綠底的小方塊，左側標籤加粗 */
.notes {
  max-width: 500px;
  margin: 16px auto 0;
  padding: 10px 20px;
  border-radius: 10px;
  background: #9ee9c659;
  text-align: left;
}

.notes p {
  margin: 0;
  color: #000000;
  font-size: 13px;
  line-height: 1.2;
}

.notes b {
  font-weight: 400;
}

/* 四區狀態燈：2 × 2，整顆貼紙（白底金框 + 往上偏移的藍框）直接用設計稿切好的背景圖 */
.zstat {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 166px));
  justify-content: center;
  gap: 12px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}

.zpill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  /* 背景圖最上緣 4px 是墊在後面的藍框，文字避開才會落在白底的正中央 */
  padding: 4px 12px 0;
  background: var(--zpill-bg) no-repeat center / 100% 100%;
  color: var(--site-orange);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.zpill.is-done {
  color: #3aa93a;
}

/* 綠色打勾用設計稿的切圖 */
.zcheck {
  width: 20px;
  height: 20px;
}

/* ---------------- 內容區 ---------------- */

.body {
  position: relative;
  flex: 1;
  /* 底部要留給頁尾的山稜線疊上來：稜線是 1440 × 175 等比縮放，
     窄螢幕上只有幾十 px，用固定值會留下一大片空白 */
  padding: 40px 0 calc(min(12.15vw, 175px) + 60px);
  background: #fff;
}

.wrap {
  max-width: var(--site-max);
  margin: 0 auto;
  padding: 0 20px;
}

/* ---------------- 分區頁籤 ---------------- */

.tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-radius: 999px;
  background: #f6f6f6;
}

.tab {
  height: 58px;
  padding: 0 8px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #202020;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color 0.25s,
    color 0.25s;
}

.tab:hover {
  color: var(--site-orange);
}

.tab.is-on {
  background: var(--site-orange);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(var(--site-orange-rgb), 0.28);
}

/* ---------------- 搜尋與排序 ---------------- */

.tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 34px;
}

.search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 400px;
  height: 46px;
  padding: 0 6px 0 16px;
  border: 1px solid #99999999;
  border-radius: 999px;
  background: #fff;
  transition: border-color 0.25s;
}

.search:focus-within {
  border-color: var(--site-orange-soft);
}

.search-ico {
  display: block;
  flex: none;
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--site-ink);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
}

.search-input::placeholder {
  color: #b3b3b3;
}

/* 清除 iOS Safari 給 type=search 的原生取消鈕，圓角膠囊裡它會突出來 */
.search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

.search-btn {
  flex: none;
  height: 36px;
  padding: 0 26px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange-soft);
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.25s;
}

.search-btn:hover {
  background: var(--site-orange);
}

.selects {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 自製 listbox（原因見 script 的說明）：收合是設計稿的白底膠囊，
   展開時邊框轉橘、箭頭翻面，選單浮在卡片之上。
   箭頭與勾勾沒有對應的素材（icon-caret.png 是首頁那支白色右箭頭），
   直接用內嵌 SVG 畫，省兩張圖也不必再發請求 */
.select-box {
  position: relative;
}

.select {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #99999999;
  border-radius: 999px;
  background: #fff;
  color: #000000;
  font-family: inherit;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color 0.25s,
    box-shadow 0.25s,
    color 0.25s;
}

.select:hover {
  border-color: var(--site-orange-soft);
}

.select:focus-visible {
  outline: none;
  border-color: var(--site-orange);
  box-shadow: 0 0 0 3px rgba(var(--site-orange-rgb), 0.16);
}

.select-box.is-open .select {
  border-color: var(--site-orange);
  color: var(--site-orange);
  box-shadow: 0 6px 18px rgba(var(--site-shadow-rgb), 0.1);
}

.select-text {
  flex: 1;
  text-align: left;
}

.select-caret {
  flex: none;
  width: 12px;
  height: 8px;
  color: #8a8a8a;
  transition:
    transform 0.25s,
    color 0.25s;
}

.select-box.is-open .select-caret {
  transform: rotate(180deg);
  color: var(--site-orange);
}

.select-menu {
  position: absolute;
  z-index: 5;
  top: calc(100% + 8px);
  right: 0;
  min-width: 100%;
  margin: 0;
  padding: 6px;
  border: 1px solid #f2e3d3;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 32px rgba(var(--site-shadow-rgb), 0.16);
  list-style: none;
}

.select-opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  color: var(--site-text);
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.2s,
    color 0.2s;
}

/* 鍵盤游標與滑鼠 hover 共用同一組樣式，兩種操作看起來才一致 */
.select-opt.is-cursor {
  background: rgba(var(--site-orange-rgb), 0.08);
  color: var(--site-orange-deep);
}

.select-opt.is-on {
  color: var(--site-orange);
  font-weight: 700;
}

.select-tick {
  flex: none;
  width: 14px;
  height: 14px;
  opacity: 0;
  transition: opacity 0.2s;
}

.select-opt.is-on .select-tick {
  opacity: 1;
}

.pop-enter-active,
.pop-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ---------------- 步道卡片 ---------------- */

/* 1180 的內容寬剛好是 3 × 368 + 2 × 38 */
.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 38px 40px;
  margin: 36px 0 0;
  padding: 0;
  list-style: none;
  transition: opacity 0.25s;
}

/* 換區與換排序時整批重抓，先淡一下比整塊跳掉自然 */
.cards.is-loading {
  opacity: 0.5;
}

/* 標籤數量不一樣時卡片會長短不齊，讓資訊區把剩下的高度吃掉，同一列的票數才會切齊底部 */
.card {
  display: flex;
  flex-direction: column;
}

.cover {
  position: relative;
  aspect-ratio: 368 / 310;
  border-radius: 20px;
  overflow: hidden;
}

.cover-link {
  display: block;
  width: 100%;
  height: 100%;
}

.cover-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.card:hover .cover-img {
  transform: scale(1.04);
}

/* 步道還沒設封面時，用貼近設計稿照片色調的漸層佔位，版面高度不變 */
.cover-empty {
  background: linear-gradient(165deg, #cfe3c2 0%, #8fb98a 45%, #4f7f6a 100%);
}

/* 設計稿是壓在封面右上角的深色圓鈕 */
.pin {
  position: absolute;
  right: 20px;
  top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.42);
  cursor: pointer;
  transition:
    background-color 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.pin:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: scale(1.06);
}

/* 已置頂的用主色標起來，一整排卡片裡看得出哪幾張是自己釘的 */
.pin.is-on {
  background: var(--site-orange);
}

.pin img {
  display: block;
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.cover-zone {
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(var(--site-shadow-rgb), 0.18);
  color: #1e1e1e;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
}

.cover-zone img {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

/* 縣市與評分同一行：縣市靠左、星等靠右（設計稿「臺北市信義區 ★ 4.8」） */
.sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 20px 0 0;
}

/* 沒填縣市的步道也要佔一行，否則同一列的卡片標題會高低不齊 */
.area {
  min-height: 20px;
  margin: 0;
  color: #8f8f8f;
  font-size: 13px;
  line-height: 1.4;
}

.rating {
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: 6px;
  margin: 0;
  color: #1e1e1e;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
}

.rating img {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.name {
  margin: 0;
  color: #02050a;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.name a {
  transition: color 0.25s;
}

.name a:hover {
  color: var(--site-orange);
}

.meta {
  flex: 1;
  margin-top: 20px;
  padding: 14px;
  border: 1px solid #fceedd;
  border-radius: 14px;
  background: linear-gradient(270.41deg, #fffbf4 -36.62%, #ffffff 129.29%);
}

.specs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
}

.spec {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* 欄位之間的細直線畫在前一組的尾巴：下一組被擠到第二行時，
   線會留在上一行行尾，而不是變成新一行的開頭 */
.spec:not(:last-child)::after {
  content: '';
  width: 1px;
  height: 18px;
  margin: 0 10px;
  background: #dddddd;
}

.specs b {
  color: #02050a;
  font-weight: 700;
}

.specs i {
  color: #808284;
  font-style: normal;
}

.tags {
  --tag-h: 36px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  /* 卡片只留一行標籤，排不下的整顆被裁掉，要看全部請進步道詳情。
     用 wrap 加單行高度而不是 nowrap，換行的標籤才會整顆消失、不會切一半 */
  max-height: var(--tag-h);
  overflow: hidden;
}

.tags li {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: var(--tag-h);
  padding: 0 16px;
  white-space: nowrap;

  border-radius: 999px;
  background: #ffe696;
  color: #724511;
  font-size: 16px;
  line-height: 1;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 20px;
}

.votes {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  color: #606367;
  font-size: 14px;
  line-height: 1;
}

.votes b {
  color: var(--site-orange-bright);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.vote-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: none;
  min-width: 163px;
  height: 52px;
  padding: 0 20px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color 0.25s,
    box-shadow 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.vote-btn img {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.vote-btn:hover:not(:disabled) {
  background: var(--site-orange-deep);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.3);
  transform: translateY(-2px);
}

/* 已投與不可投票時維持橘色系但轉淡，一眼看得出是同一顆鈕的另一個狀態 */
.vote-btn:disabled {
  background: #f7b98c;
  cursor: not-allowed;
}

.empty {
  margin: 80px 0;
  color: var(--site-text-weak);
  font-size: 16px;
  text-align: center;
}

/* ---------------- 投票成功彈窗 ---------------- */

.dlg-mask {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* 設計稿的白色頁首被壓成 #7f7f7f，反推遮罩就是五成黑（與登入彈窗一致） */
  background: rgba(0, 0, 0, 0.5);
}

.dlg-panel {
  width: 100%;
  max-width: 340px;
  padding: 36px 24px 32px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(var(--site-shadow-rgb), 0.28);
  text-align: center;
}

.dlg-art {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 130px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: #faf5ec;
  overflow: hidden;
}

.dlg-art img {
  display: block;
  width: 128px;
  height: 128px;
  object-fit: contain;
}

.dlg-title {
  margin: 0;
  color: #111111;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
}

.dlg-text {
  margin: 12px 0 0;
  color: #333333;
  font-size: 15px;
  line-height: 1.6;
}

.dlg-text b {
  color: var(--site-orange);
  font-weight: 700;
}

.dlg-btn {
  width: 100%;
  height: 56px;
  margin-top: 26px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.25s;
}

.dlg-btn:hover {
  background: var(--site-orange-deep);
}

.dlg-enter-active,
.dlg-leave-active {
  transition: opacity 0.25s ease;
}

.dlg-enter-active .dlg-panel,
.dlg-leave-active .dlg-panel {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dlg-enter-from,
.dlg-leave-to {
  opacity: 0;
}

.dlg-enter-from .dlg-panel,
.dlg-leave-to .dlg-panel {
  transform: translateY(16px) scale(0.96);
}

/* ---------------- 窄螢幕 ---------------- */

/* 一列三張到 1180 以下就太窄，先讓卡片降成兩張 */
@media (max-width: 1180px) {
  .cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 36px 28px;
  }
}

/* 人物在這個寬度以下會壓到說明文字，直接收掉 */
@media (max-width: 1100px) {
  .hiker {
    display: none;
  }
}

@media (max-width: 900px) {
  .banner {
    min-height: 0;
    padding: 48px 20px 44px;
  }

  /* 底圖被裁得只剩中段時字會壓在草地上，墊一層白暈把文字區托起來 */
  .hero {
    padding: 18px 12px 22px;
    border-radius: 24px;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.95) 55%,
      rgba(255, 255, 255, 0) 78%
    );
  }

  .title {
    gap: 8px;
    letter-spacing: 1px;
  }

  .title-heart {
    width: 20px;
    margin-top: 4px;
  }

  .title-eyes {
    width: 34px;
  }

  .period {
    margin-top: 12px;
  }

  .notes {
    margin-top: 14px;
    padding: 12px 14px;
  }

  .notes p {
    font-size: 13px;
    line-height: 1.6;
  }

  .zstat {
    margin-top: 18px;
  }

  .body {
    padding-top: 28px;
  }

  .tools {
    flex-wrap: wrap;
  }

  .search {
    max-width: none;
  }

  .selects {
    width: 100%;
  }

  /* 窄版下拉撐滿一行，選單跟著同寬 */
  .select-box {
    flex: 1;
    min-width: 0;
  }

  .select {
    width: 100%;
    /* 手指按的目標放大到 44px */
    height: 44px;
  }

  .select-opt {
    height: 44px;
  }
}

@media (max-width: 767px) {
  .tab {
    height: 46px;
    font-size: 14px;
  }

  .zstat {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .zpill {
    height: 36px;
    padding: 4px 10px 0;
    font-size: 13px;
  }

  .zcheck {
    width: 15px;
    height: 15px;
  }

  .cards {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-top: 28px;
  }

  .cover {
    aspect-ratio: 16 / 11;
  }

  .name {
    font-size: 20px;
  }

  .meta {
    padding: 14px;
  }

  .spec:not(:last-child)::after {
    margin: 0 8px;
  }

  .tags {
    --tag-h: 32px;
  }

  .tags li {
    padding: 0 14px;
    font-size: 13px;
  }

  .votes b {
    font-size: 26px;
  }

  .vote-btn {
    min-width: 0;
    flex: 1;
    max-width: 180px;
    height: 44px;
    font-size: 15px;
  }

  .dlg-panel {
    padding: 28px 20px 24px;
  }

  .dlg-art {
    width: 108px;
    height: 108px;
  }

  .dlg-art img {
    width: 104px;
    height: 104px;
  }
}

/* 極窄螢幕：票數與按鈕併排會把兩邊都擠扁，改成上下兩行 */
@media (max-width: 400px) {
  .foot {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }

  /* 改直式後主軸換成垂直，flex:1 的 flex-basis:0 會蓋掉 height 把鈕壓扁，這裡收回來 */
  .vote-btn {
    flex: none;
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vote-btn,
  .pin,
  .select-caret,
  .pop-enter-active,
  .pop-leave-active,
  .dlg-enter-active,
  .dlg-leave-active,
  .dlg-enter-active .dlg-panel,
  .dlg-leave-active .dlg-panel {
    transition: none;
  }

  .vote-btn:hover:not(:disabled),
  .pin:hover {
    transform: none;
  }
}
</style>
